import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CheckIcon from '@mui/icons-material/Check';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGetMyClubsQuery, useSwitchClubMutation } from '../api/apiSlice';
import { useAppDispatch, useDecodedToken } from '../app/hooks';
import { setToken } from '../features/auth/authSlice';

// Lets a user who belongs to more than one club change which one is
// "active" (the club id/role embedded in their JWT) without logging out --
// switchClub just re-issues a token scoped to a different membership. Only
// approved memberships are selectable; a pending one shows up so the user
// knows it exists, but can't be switched to yet. A "join a club" entry is
// always appended at the end so browsing the directory is discoverable from
// anywhere, not just the club-search page.
//
// Since the outings / races / events feeds are now common across every club
// a member belongs to, plain members and animators have no "active club" to
// pick -- this switcher only exists to choose which club to *manage*, so it
// renders only for users who are an admin of at least one club.
export default function ClubSwitcher() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const decodedToken = useDecodedToken();
    const { data: clubs = [] } = useGetMyClubsQuery();
    const [switchClub] = useSwitchClubMutation();

    const currentClub = clubs.find((club) => club.CLUB_ID === decodedToken?.clubId);
    const isClubAdmin = clubs.some((club) => club.ROLE === 'ADMIN');

    const handleSelect = async (clubId: number) => {
        setAnchorEl(null);
        if (clubId === decodedToken?.clubId) return;

        try {
            const token = await switchClub(clubId).unwrap();
            dispatch(setToken(token));
            navigate('/activities');
        } catch {
            // Swallow -- worst case the switch silently doesn't happen and
            // the user stays on their current club, nothing destructive.
        }
    };

    const handleJoinClub = () => {
        setAnchorEl(null);
        navigate('/clubs');
    };

    if (!isClubAdmin) {
        return null;
    }

    return (
        <>
            <Button
                onClick={(e) => setAnchorEl(e.currentTarget)}
                endIcon={<ExpandMoreIcon />}
                sx={{ color: 'white', textTransform: 'none' }}
            >
                {currentClub?.NAME ?? t(clubs.length === 0 ? 'clubSwitcher.joinClub' : 'clubSwitcher.label')}
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {clubs.map((club) => (
                    <MenuItem
                        key={club.CLUB_ID}
                        onClick={() => club.ROLE && handleSelect(club.CLUB_ID)}
                        disabled={!club.ROLE}
                        selected={club.CLUB_ID === decodedToken?.clubId}
                    >
                        <ListItemIcon>
                            {club.CLUB_ID === decodedToken?.clubId ? (
                                <CheckIcon fontSize="small" />
                            ) : !club.ROLE ? (
                                <HourglassEmptyIcon fontSize="small" />
                            ) : null}
                        </ListItemIcon>
                        <ListItemText
                            primary={club.NAME}
                            secondary={!club.ROLE ? t('clubSwitcher.pending') : undefined}
                        />
                    </MenuItem>
                ))}
                {clubs.length > 0 && <Divider />}
                <MenuItem onClick={handleJoinClub}>
                    <ListItemIcon>
                        <AddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t(clubs.length === 0 ? 'clubSwitcher.joinClub' : 'clubSwitcher.joinAnother')} />
                </MenuItem>
            </Menu>
        </>
    );
}
